import React, { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Filter,
  Loader2,
} from "lucide-react";
import { AttributeReviewRow } from "@/components/catalog/AttributeReviewRow";
import { Card, CardContent } from "@/components/ui";
import { getAttributeReviewGridClass } from "@/lib/attributeReview/getAttributeReviewGridClass";
import { isRejectedAttributeReviewStatus } from "@/lib/attributeReview/reviewQueueStatus";
import { cn } from "@/lib/utils";

import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview";

import type { AttributeReviewTableProps } from "./AttributeReviewTable.types";

export const AttributeReviewTable: React.FC<AttributeReviewTableProps> = ({
  sessionId,
  mappings,
  confidenceSortDirection,
  mlSuggestedStatusFilter,
  mlSuggestedStatusFilterOptions,
  expandedId,
  onToggleExpanded,
  onToggleConfidenceSort,
  onMlSuggestedStatusFilterChange,
  onStageApproval,
  onApproveRowImmediately,
  onSaveRow,
  onCommitBucket,
  onDiscardBucket,
  onRevertStaged,
  approvedBucketIds,
  approvedBucketSize,
  isCommittingBucket,
  approvalPipeline,
  approvingFieldIds,
  onDraftReviewChange,
  selectedIds,
  allSelected,
  onSelectAll,
  onSelectChange,
  isLoading,
  showSelection: showSelectionOverride,
  isPreApproved = false,
  showConfidence = true,
  onRejectField,
  onUnrejectField,
  draftReviewSelections,
}) => {
  const showSelection = showSelectionOverride ?? !isPreApproved;
  const visibleMappingIds = mappings.map((mapping) => String(mapping.id));
  const [filterOpen, setFilterOpen] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement | null>(null);
  const selectedStatusLabel =
    mlSuggestedStatusFilter === "ALL" ? null : mlSuggestedStatusFilter;

  useEffect(() => {
    if (!filterOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target as Node)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [filterOpen]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-20 flex flex-col items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-4" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading field mappings...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (mappings.length === 0) {
    return null;
  }

  return (
    <div className="w-full overflow-x-auto">
      <div
        className={cn(
          "grid gap-4 px-4 py-3 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 min-w-[760px]",
          getAttributeReviewGridClass(showSelection, showConfidence),
        )}
      >
        {showSelection && (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => onSelectAll(e.target.checked)}
              data-testid="select-all-checkbox"
              className={cn(
                "w-4 h-4 rounded border-slate-300 dark:border-slate-600",
                "text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0",
                "cursor-pointer",
              )}
            />
          </div>
        )}
        <div>Source Attribute</div>
        <div aria-hidden />
        <div className="relative flex items-center gap-2 min-w-0">
          <span className="flex-shrink-0">ML Suggested</span>
          <div ref={filterMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setFilterOpen((open) => !open)}
              className={cn(
                "inline-flex h-7 items-center gap-1.5 rounded border px-2",
                "border-slate-200 bg-white text-slate-500 hover:text-slate-800",
                "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:text-slate-100",
                "focus:outline-none focus:ring-1 focus:ring-indigo-500",
                selectedStatusLabel &&
                  "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300",
              )}
              aria-expanded={filterOpen}
              aria-haspopup="menu"
              aria-label={
                selectedStatusLabel
                  ? `Filter ML suggested status: ${selectedStatusLabel}`
                  : "Filter ML suggested status"
              }
              data-testid="ml-suggested-status-filter-button"
            >
              <Filter className="w-3.5 h-3.5" />
              {selectedStatusLabel && (
                <span className="max-w-[120px] truncate normal-case tracking-normal">
                  {selectedStatusLabel}
                </span>
              )}
            </button>
            {filterOpen && (
              <div
                role="menu"
                className={cn(
                  "absolute left-0 top-8 z-30 w-64 overflow-hidden rounded-lg border",
                  "border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900",
                )}
              >
                <div className="border-b border-slate-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  Status type
                </div>
                {selectedStatusLabel && (
                  <div className="border-b border-slate-100 bg-indigo-50 px-3 py-2 text-xs normal-case tracking-normal text-indigo-700 dark:border-slate-800 dark:bg-indigo-950/40 dark:text-indigo-300">
                    Selected: {selectedStatusLabel}
                  </div>
                )}
                {[
                  { id: "ALL", name: "All statuses", count: null },
                  ...mlSuggestedStatusFilterOptions,
                ].map((option) => {
                  const isSelected = option.id === mlSuggestedStatusFilter;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={isSelected}
                      onClick={() => {
                        onMlSuggestedStatusFilterChange(option.id);
                        setFilterOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center gap-2 px-3 py-2 text-left normal-case tracking-normal",
                        "text-xs text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800",
                        isSelected &&
                          "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300",
                      )}
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        {isSelected && <Check className="w-3.5 h-3.5" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {option.name}
                      </span>
                      {option.count != null && (
                        <span className="text-[11px] text-slate-400 dark:text-slate-500">
                          {option.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {showConfidence && (
          <div>
            <button
              type="button"
              onClick={onToggleConfidenceSort}
              className="inline-flex items-center gap-1 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
              data-testid="confidence-sort-button"
              aria-label={
                confidenceSortDirection === null
                  ? "Sort by AI band and registry status"
                  : `Sort by AI band and registry status ${confidenceSortDirection === "desc" ? "descending" : "ascending"}`
              }
            >
              <span>AI / Registry</span>
              {confidenceSortDirection === "desc" ? (
                <ArrowDown className="w-3.5 h-3.5" />
              ) : confidenceSortDirection === "asc" ? (
                <ArrowUp className="w-3.5 h-3.5" />
              ) : (
                <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
              )}
            </button>
          </div>
        )}
        <div className="text-right">Actions</div>
      </div>
      <div data-testid="mapping-rows" className="space-y-0">
        {mappings.map((mapping, idx) => {
          const prevId = idx > 0 ? String(mappings[idx - 1].id ?? "") : null;
          const nextId =
            idx < mappings.length - 1
              ? String(mappings[idx + 1].id ?? "")
              : null;
          return (
            <AttributeReviewRow
              key={`${sessionId}:${mapping.id as string}`}
              sessionId={sessionId}
              mapping={mapping as Record<string, unknown>}
              isExpanded={expandedId === (mapping.id as string)}
              onToggle={() => onToggleExpanded(mapping.id as string)}
              onStageApproval={onStageApproval}
              onApproveRowImmediately={onApproveRowImmediately}
              onSaveRow={onSaveRow}
              onCommitBucket={onCommitBucket}
              onDiscardBucket={onDiscardBucket}
              onRevertStaged={onRevertStaged}
              approvedBucketIds={approvedBucketIds}
              approvedBucketSize={approvedBucketSize}
              isCommittingBucket={isCommittingBucket}
              approvalPipeline={approvalPipeline}
              isApproving={!!approvingFieldIds?.has(String(mapping.id))}
              onDraftReviewChange={onDraftReviewChange}
              draftSelection={draftReviewSelections[String(mapping.id)]}
              showCheckbox={showSelection}
              showConfidence={showConfidence}
              isSelected={selectedIds.has(mapping.id as string)}
              onSelectChange={(id, checked, shiftKey) =>
                onSelectChange(id, checked, shiftKey, visibleMappingIds)
              }
              isPreApproved={isPreApproved}
              isRejected={isRejectedAttributeReviewStatus(
                mapping.decisionStatus,
              )}
              onUnreject={
                onUnrejectField
                  ? () => onUnrejectField(mapping as AttributeReviewMappingRow)
                  : undefined
              }
              onRejectField={onRejectField}
              hasPrev={!!prevId}
              hasNext={!!nextId}
              onNavigatePrev={
                prevId ? () => onToggleExpanded(prevId) : undefined
              }
              onNavigateNext={
                nextId ? () => onToggleExpanded(nextId) : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
};
